import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Film, Globe, Shield, ScrollText, Activity, AlertTriangle,
  ArrowDown, ArrowUp, Eye, Heart, MessageSquare, Server, Smartphone, Monitor, Tablet,
  Chrome, Wifi, Clock, TrendingUp, Download, RefreshCw, Search
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { timeAgo } from "@/lib/time";
import { toast } from "sonner";

const TABS = [
  { k: "overview", label: "Overview", Icon: LayoutDashboard },
  { k: "users", label: "Users", Icon: Users },
  { k: "content", label: "Content", Icon: Film },
  { k: "traffic", label: "Traffic", Icon: Globe },
  { k: "realtime", label: "Real-time", Icon: Activity },
  { k: "security", label: "Security", Icon: Shield },
  { k: "audit", label: "Audit", Icon: ScrollText },
  { k: "errors", label: "Errors", Icon: AlertTriangle },
];

export default function Stats() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!user) navigate("/login");
      else if (user.role !== "admin") navigate("/");
    }
  }, [user, loading, navigate]);

  const fetchTab = async (k) => {
    setBusy(true);
    try {
      const { data: d } = await api.get(`/admin/stats/${k}`);
      setData((s) => ({ ...s, [k]: d }));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (user?.role === "admin") fetchTab(tab);
    // eslint-disable-next-line
  }, [tab, user]);

  // Auto-refresh realtime every 10s
  useEffect(() => {
    if (tab !== "realtime") return;
    const i = setInterval(() => fetchTab("realtime"), 10000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [tab]);

  const exportCsv = () => {
    const d = data[tab];
    if (!d) return;
    const json = JSON.stringify(d, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `savageboy-stats-${tab}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Exported");
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="stats-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#E50914] font-bold inline-flex items-center gap-1.5">
            <LayoutDashboard className="w-3 h-3" /> Statistics
          </span>
          <h1 className="font-display text-4xl font-black mt-1">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => fetchTab(tab)} disabled={busy} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold">
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={exportCsv} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-bold">
            <Download className="w-4 h-4" /> Export JSON
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.k}
            data-testid={`stats-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-3 text-xs sm:text-sm font-bold uppercase tracking-widest flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
              tab === t.k ? "border-[#E50914] text-white" : "border-transparent text-zinc-500 hover:text-white"
            }`}
          >
            <t.Icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {tab === "overview" && <OverviewTab d={data.overview} />}
        {tab === "users" && <UsersTab d={data.users} />}
        {tab === "content" && <ContentTab d={data.content} />}
        {tab === "traffic" && <TrafficTab d={data.traffic} />}
        {tab === "realtime" && <RealtimeTab d={data.realtime} />}
        {tab === "security" && <SecurityTab d={data.security} />}
        {tab === "audit" && <AuditTab d={data.audit} />}
        {tab === "errors" && <ErrorsTab d={data.errors} />}
      </div>
    </div>
  );
}

/* ================= KPI & charts ================= */

function KPI({ icon: Icon, label, value, sub, color = "#E50914", testid }) {
  return (
    <div data-testid={testid} className="glass rounded-2xl p-5 relative overflow-hidden">
      <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-3xl pointer-events-none" style={{ background: `${color}33` }} />
      <div className="relative flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center border" style={{ background: `${color}1a`, borderColor: `${color}55`, color }}>
          <Icon className="w-5 h-5" />
        </div>
        {sub != null && <Trend value={sub} />}
      </div>
      <div className="relative">
        <div className="font-display font-black text-3xl" style={{ color }}>
          {typeof value === "number" ? value.toLocaleString() : (value ?? "—")}
        </div>
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-bold mt-1">{label}</div>
      </div>
    </div>
  );
}

function Trend({ value }) {
  const v = Number(value);
  if (isNaN(v) || v === 0) return null;
  const up = v > 0;
  return (
    <span className={`text-xs font-bold inline-flex items-center gap-0.5 px-2 py-1 rounded-md ${up ? "bg-[#32D74B]/10 text-[#32D74B]" : "bg-[#E50914]/10 text-[#E50914]"}`}>
      {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(v)}%
    </span>
  );
}

function LineChart({ data, color = "#E50914", testid }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  const w = 600, h = 140, p = 20;
  const stepX = (w - p * 2) / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = p + i * stepX;
    const y = h - p - ((d.count / max) * (h - p * 2));
    return [x, y];
  });
  const path = points.map((pt, i) => (i === 0 ? "M" : "L") + pt.join(",")).join(" ");
  const area = path + ` L ${points[points.length - 1]?.[0]},${h - p} L ${points[0]?.[0]},${h - p} Z`;
  return (
    <div className="w-full overflow-x-auto" data-testid={testid}>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full min-w-[480px]">
        <defs>
          <linearGradient id={`g-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.45" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill={`url(#g-${color.replace("#", "")})`} />
        <path d={path} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="3" fill={color} />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-zinc-500 px-5 mt-1">
        {data.length > 0 && (
          <>
            <span>{data[0].date}</span>
            <span>{data[Math.floor(data.length / 2)]?.date}</span>
            <span>{data[data.length - 1].date}</span>
          </>
        )}
      </div>
    </div>
  );
}

function BarList({ items, color = "#E50914", labelKey = "label", valueKey = "count", testid }) {
  const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-semibold text-zinc-200 truncate pr-2">{it[labelKey] || "—"}</span>
            <span className="text-zinc-400 font-bold tabular-nums">{Number(it[valueKey] || 0).toLocaleString()}</span>
          </div>
          <div className="h-2 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${((it[valueKey] || 0) / max) * 100}%`, background: `linear-gradient(90deg, ${color}, ${color}99)` }} />
          </div>
        </div>
      ))}
      {items.length === 0 && <p className="text-sm text-zinc-500">No data yet.</p>}
    </div>
  );
}

function Donut({ items, total, colors = ["#E50914", "#FF9F0A", "#32D74B", "#0a84ff", "#bf5af2", "#5e5ce6"], testid }) {
  const sum = total ?? items.reduce((a, b) => a + (b.count || 0), 0);
  let cum = 0;
  const r = 42, c = 50;
  const circ = 2 * Math.PI * r;
  return (
    <div className="flex items-center gap-5" data-testid={testid}>
      <svg viewBox="0 0 100 100" className="w-32 h-32 -rotate-90">
        <circle cx={c} cy={c} r={r} fill="none" stroke="#1a1a1a" strokeWidth="12" />
        {items.map((it, i) => {
          const frac = sum > 0 ? (it.count || 0) / sum : 0;
          const len = frac * circ;
          const offset = (cum / sum) * circ;
          cum += it.count || 0;
          return (
            <circle key={i} cx={c} cy={c} r={r} fill="none"
              stroke={colors[i % colors.length]} strokeWidth="12"
              strokeDasharray={`${len} ${circ - len}`} strokeDashoffset={-offset}
            />
          );
        })}
      </svg>
      <div className="space-y-1.5 flex-1 min-w-0">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between gap-2 text-xs">
            <span className="flex items-center gap-2 truncate">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: colors[i % colors.length] }} />
              <span className="capitalize text-zinc-200">{it.label || "—"}</span>
            </span>
            <span className="text-zinc-400 font-bold">{(it.count || 0).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-lg">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/* ================= TABS ================= */

function OverviewTab({ d }) {
  if (!d) return <Loading />;
  return (
    <>
      {d.alerts?.length > 0 && (
        <div className="space-y-2">
          {d.alerts.map((a, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${a.level === "critical" ? "bg-[#E50914]/10 border-[#E50914]/40 text-[#E50914]" : "bg-[#FF9F0A]/10 border-[#FF9F0A]/40 text-[#FF9F0A]"}`}>
              <AlertTriangle className="w-4 h-4" /><span className="text-sm font-semibold">{a.msg}</span>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-total-users" icon={Users} label="Total users" value={d.total_users} color="#E50914" />
        <KPI testid="kpi-active-today" icon={Activity} label="Active today" value={d.active_users_today} color="#FF9F0A" />
        <KPI testid="kpi-online-now" icon={Wifi} label="Online now" value={d.online_now} color="#32D74B" />
        <KPI testid="kpi-new-today" icon={TrendingUp} label="New today" value={d.new_users_today} sub={d.growth_rate_pct} color="#0a84ff" />
        <KPI testid="kpi-page-views" icon={Eye} label="Page views (24h)" value={d.total_page_views} color="#bf5af2" />
        <KPI testid="kpi-total-views" icon={Eye} label="Video views (all)" value={d.total_views} color="#E50914" />
        <KPI testid="kpi-total-likes" icon={Heart} label="Total likes" value={d.total_likes} color="#FF9F0A" />
        <KPI testid="kpi-avg-resp" icon={Server} label="Avg API ms" value={d.avg_api_response_ms} color="#5e5ce6" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Film} label="Videos" value={d.total_videos} color="#E50914" />
        <KPI icon={MessageSquare} label="Comments" value={d.total_comments} color="#FF9F0A" />
        <KPI icon={AlertTriangle} label="Failed logins 24h" value={d.failed_logins_24h} color="#E50914" />
        <KPI icon={AlertTriangle} label="5xx errors 24h" value={d.errors_5xx_24h} color="#E50914" />
      </div>
    </>
  );
}

function UsersTab({ d }) {
  if (!d) return <Loading />;
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section title="Registrations (30d)"><LineChart data={d.registrations_30d} testid="chart-regs" /></Section>
        <Section title="By auth provider"><Donut items={d.by_provider} testid="donut-provider" /></Section>
        <Section title="By role"><Donut items={d.by_role} testid="donut-role" /></Section>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Most active users (by likes)">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                <th className="pb-2">Name</th><th>Email</th><th>Likes</th><th>Last login</th>
              </tr></thead>
              <tbody>
                {d.most_active.map((u) => (
                  <tr key={u.id} className="border-t border-white/5">
                    <td className="py-2 font-semibold">{u.name}</td>
                    <td className="text-zinc-400">{u.email}</td>
                    <td className="text-[#E50914] font-bold">{u.likes_count}</td>
                    <td className="text-zinc-500 text-xs">{u.last_login ? timeAgo(u.last_login) : "never"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
        <Section title="Recent logins">
          <div className="space-y-2">
            {d.recent_logins.map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm">
                <span className="font-semibold">{u.name}</span>
                <span className="text-zinc-400 text-xs">{u.auth_provider}</span>
                <span className="text-zinc-500 text-xs">{timeAgo(u.last_login)}</span>
              </div>
            ))}
            {d.recent_logins.length === 0 && <p className="text-sm text-zinc-500">No recent logins.</p>}
          </div>
        </Section>
      </div>
    </>
  );
}

function ContentTab({ d }) {
  if (!d) return <Loading />;
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section title="Videos by status"><Donut items={d.by_status} testid="donut-vid-status" /></Section>
        <Section title="Performance by route"><BarList items={d.per_route} labelKey="route" valueKey="views" testid="bar-routes" /></Section>
        <Section title="Top liked"><BarList items={d.top_liked.map((v) => ({ label: v.title, count: v.likes }))} color="#FF9F0A" testid="bar-liked" /></Section>
      </div>
      <Section title="Top viewed videos">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500"><th className="pb-2">Title</th><th>Route</th><th>Status</th><th>Views</th><th>Likes</th></tr></thead>
            <tbody>
              {d.top_viewed.map((v) => (
                <tr key={v.id} className="border-t border-white/5">
                  <td className="py-2 font-semibold">{v.title}</td>
                  <td className="text-zinc-400 capitalize">{v.route_id}</td>
                  <td className="text-xs uppercase tracking-widest">{v.status}</td>
                  <td className="font-bold">{(v.views || 0).toLocaleString()}</td>
                  <td className="text-[#E50914] font-bold">{(v.likes || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title="Most commented">
        <BarList items={d.top_commented.map((v) => ({ label: v.title || v.id, count: v.comments }))} color="#0a84ff" testid="bar-commented" />
      </Section>
    </>
  );
}

function TrafficTab({ d }) {
  if (!d) return <Loading />;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Eye} label="Page views 7d" value={d.page_views_7d} color="#E50914" />
        <KPI icon={Users} label="Unique visitors 7d" value={d.unique_visitors_7d} color="#FF9F0A" />
        <KPI icon={Monitor} label="Desktop" value={d.devices.find((x) => x.label === "desktop")?.count || 0} color="#32D74B" />
        <KPI icon={Smartphone} label="Mobile + Tablet" value={(d.devices.find((x) => x.label === "mobile")?.count || 0) + (d.devices.find((x) => x.label === "tablet")?.count || 0)} color="#0a84ff" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Visits (last 14 days)"><LineChart data={d.visits_14d} testid="chart-visits" /></Section>
        <Section title="Most visited paths"><BarList items={d.most_visited} labelKey="path" testid="bar-paths" /></Section>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Section title="Devices"><Donut items={d.devices} testid="donut-device" /></Section>
        <Section title="Browsers"><Donut items={d.browsers} testid="donut-browser" /></Section>
        <Section title="Operating systems"><Donut items={d.os} testid="donut-os" /></Section>
      </div>
      <Section title="Traffic sources"><BarList items={d.sources} color="#bf5af2" testid="bar-sources" /></Section>
    </>
  );
}

function RealtimeTab({ d }) {
  if (!d) return <Loading />;
  return (
    <>
      <div className="flex items-center gap-3 mb-2">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#32D74B] opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-[#32D74B]" />
        </span>
        <span className="text-sm font-bold uppercase tracking-widest text-zinc-300">Live · auto-refresh 10s</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Section title={`Online now (${d.online_count})`}>
          <div className="space-y-2">
            {d.online_users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/5">
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E50914] to-[#7a0309] flex items-center justify-center text-xs font-black uppercase">
                  {u.avatar ? <img src={u.avatar} alt="" className="w-full h-full rounded-full object-cover" /> : (u.name || "?")[0]}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{u.name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{u.email}</div>
                </div>
                {u.role === "admin" && <span className="text-[10px] font-bold text-[#E50914]">ADMIN</span>}
              </div>
            ))}
            {d.online_users.length === 0 && <p className="text-sm text-zinc-500">No users online right now.</p>}
          </div>
        </Section>
        <Section title="Recent logins (1h)">
          <ul className="space-y-2 text-sm">
            {d.recent_logins.map((e, i) => (
              <li key={i} className="flex items-center justify-between gap-2">
                <span className="truncate">{e.email}</span>
                <span className="text-[11px] text-zinc-500 shrink-0">{timeAgo(e.ts)}</span>
              </li>
            ))}
            {d.recent_logins.length === 0 && <p className="text-sm text-zinc-500">None.</p>}
          </ul>
        </Section>
        <Section title="Recent signups (1h)">
          <ul className="space-y-2 text-sm">
            {d.recent_signups.map((e, i) => (
              <li key={i} className="flex items-center justify-between gap-2">
                <span className="truncate">{e.email}</span>
                <span className="text-[11px] text-zinc-500 shrink-0">{timeAgo(e.ts)}</span>
              </li>
            ))}
            {d.recent_signups.length === 0 && <p className="text-sm text-zinc-500">None.</p>}
          </ul>
        </Section>
      </div>
      <Section title="Live activity feed (last hour)">
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0a0a0a]"><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500"><th className="pb-2">Time</th><th>Method</th><th>Path</th><th>Status</th><th>IP</th><th>Device</th><th>Browser</th></tr></thead>
            <tbody>
              {d.recent_visits.map((v, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-1.5 text-zinc-400">{timeAgo(v.ts)}</td>
                  <td className="text-zinc-400">{v.method}</td>
                  <td className="text-zinc-200 truncate max-w-[180px]">{v.path}</td>
                  <td className={`font-bold ${v.status >= 500 ? "text-[#E50914]" : v.status >= 400 ? "text-[#FF9F0A]" : "text-[#32D74B]"}`}>{v.status}</td>
                  <td className="text-zinc-500">{v.ip}</td>
                  <td className="text-zinc-500 capitalize">{v.device}</td>
                  <td className="text-zinc-500">{v.browser}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}

function SecurityTab({ d }) {
  if (!d) return <Loading />;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI icon={Shield} label="Login success 24h" value={d.success_24h} color="#32D74B" />
        <KPI icon={AlertTriangle} label="Failed attempts 24h" value={d.failed_24h} color="#E50914" />
        <KPI icon={Wifi} label="Top hostile IPs" value={d.top_failing_ips.length} color="#FF9F0A" />
        <KPI icon={Activity} label="Auth events" value={d.recent_failed.length + d.recent_success.length} color="#0a84ff" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Top failing IPs (24h)"><BarList items={d.top_failing_ips} labelKey="ip" valueKey="attempts" testid="bar-failips" /></Section>
        <Section title="Recent failed attempts">
          <div className="space-y-1.5 text-xs max-h-80 overflow-y-auto">
            {d.recent_failed.map((e, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 p-2 rounded-lg bg-[#E50914]/5 border border-[#E50914]/20">
                <span className="truncate font-semibold text-zinc-200">{e.email}</span>
                <span className="text-zinc-500">{e.ip}</span>
                <span className="text-zinc-500">{timeAgo(e.ts)}</span>
              </div>
            ))}
            {d.recent_failed.length === 0 && <p className="text-sm text-zinc-500">None.</p>}
          </div>
        </Section>
      </div>
      <Section title="Recent successful logins">
        <div className="space-y-1.5 text-xs max-h-80 overflow-y-auto">
          {d.recent_success.map((e, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-2 p-2 rounded-lg bg-[#32D74B]/5 border border-[#32D74B]/20">
              <span className="truncate font-semibold text-zinc-200">{e.email}</span>
              <span className="text-zinc-500">{e.ip}</span>
              <span className="text-zinc-500">{timeAgo(e.ts)}</span>
            </div>
          ))}
          {d.recent_success.length === 0 && <p className="text-sm text-zinc-500">None.</p>}
        </div>
      </Section>
    </>
  );
}

function AuditTab({ d }) {
  const [q, setQ] = useState("");
  if (!d) return <Loading />;
  const filtered = q.trim()
    ? d.filter((e) => (e.action || "").includes(q) || (e.actor_email || "").includes(q) || (e.target_id || "").includes(q))
    : d;
  return (
    <>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by action / email / id..." className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
      </div>
      <Section title={`Admin actions (${filtered.length})`}>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0a0a0a]"><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500"><th className="pb-2">Time</th><th>Actor</th><th>Action</th><th>Target</th><th>IP</th><th>Details</th></tr></thead>
            <tbody>
              {filtered.map((e, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-1.5 text-zinc-400 whitespace-nowrap">{timeAgo(e.ts)}</td>
                  <td className="text-zinc-200">{e.actor_email}</td>
                  <td className="font-bold text-[#E50914]">{e.action}</td>
                  <td className="text-zinc-400">{e.target_type}:{e.target_id?.slice(0, 12)}</td>
                  <td className="text-zinc-500">{e.ip}</td>
                  <td className="text-zinc-500 max-w-[200px] truncate">{e.details ? JSON.stringify(e.details) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}

function ErrorsTab({ d }) {
  if (!d) return <Loading />;
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {d.by_status.map((x) => (
          <KPI key={x.status} icon={AlertTriangle} label={`HTTP ${x.status}`} value={x.count} color={x.status >= 500 ? "#E50914" : "#FF9F0A"} />
        ))}
        {d.by_status.length === 0 && (
          <div className="col-span-full glass rounded-2xl p-8 text-center text-zinc-500 text-sm">No errors in last 7 days.</div>
        )}
      </div>
      <Section title="Recent errors">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[#0a0a0a]"><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500"><th className="pb-2">Time</th><th>Status</th><th>Method</th><th>Path</th><th>IP</th></tr></thead>
            <tbody>
              {d.recent.map((e, i) => (
                <tr key={i} className="border-t border-white/5">
                  <td className="py-1.5 text-zinc-400">{timeAgo(e.ts)}</td>
                  <td className={`font-bold ${e.status >= 500 ? "text-[#E50914]" : "text-[#FF9F0A]"}`}>{e.status}</td>
                  <td className="text-zinc-400">{e.method}</td>
                  <td className="text-zinc-200 truncate max-w-[260px]">{e.path}</td>
                  <td className="text-zinc-500">{e.ip}</td>
                </tr>
              ))}
              {d.recent.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-zinc-500">No errors logged.</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>
    </>
  );
}

function Loading() {
  return <div className="glass rounded-2xl p-10 text-center text-zinc-500 text-sm">Loading...</div>;
}
